import * as React from "react";

/**
 * Cloudflare Turnstile - the script, the switch and the token, without the DOM.
 *
 * ── Why a challenge at all, and why it is not where you would expect ───────
 *
 * Sign-in, sign-up, the code resend and the password reset all go from this
 * browser **straight to GoTrue**. None of them touch `web-app/api`, so every
 * rate limiter and every piece of middleware we own is on the wrong side of the
 * request: today those four endpoints are open to anyone with a script, and the
 * two that send email are open against a project-wide quota every other flow
 * shares. The main product guards the equivalent routes with a
 * `requireTurnstile()` Express middleware; that shape simply does not exist
 * here, because there is no Express route in the path.
 *
 * What is in the path is GoTrue itself, which verifies a captcha natively when
 * `[auth.captcha]` is enabled in `supabase/config.toml`. So the whole
 * integration is: mint a token in the browser, pass it as `options.captchaToken`
 * (`lib/auth.tsx`), and let GoTrue call Cloudflare. **Our API is not involved at
 * any point and must not grow a `/verify-captcha` route** - that would be a
 * second, unenforceable check beside the real one.
 *
 * ── Two switches, and the order they are thrown in matters ─────────────────
 *
 * `VITE_TURNSTILE_SITE_KEY` here, `[auth.captcha]` there.
 *
 *   client on, server off  → widget renders, token is sent, GoTrue ignores it.
 *                            Harmless. **Deploy this state first.**
 *   client off, server on  → every sign-in fails with `captcha_failed`,
 *                            **the phone's included**. GoTrue's captcha is a
 *                            project-wide setting, not a per-route one, so
 *                            turning it on locks out every client that cannot
 *                            produce a token. There is no partial rollout.
 *   both off               → exactly today's behaviour.
 *
 * That asymmetry is why the site key lives in the environment rather than being
 * hard-coded: a build can be shipped with the widget live and the project still
 * unprotected, which is the only safe sequence.
 */

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_ID = "cf-turnstile";

export interface TurnstileApi {
  render: (el: HTMLElement, options: Record<string, unknown>) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export const TURNSTILE_SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

/** Whether this build challenges at all. Blank key means the feature is off, not broken. */
export const turnstileEnabled = TURNSTILE_SITE_KEY !== "";

let scriptPromise: Promise<void> | null = null;

/**
 * `render=explicit`, so Turnstile does not scan the document and auto-mount
 * anything - the component decides when a widget exists.
 *
 * Module-level, because the sign-up flow renders a challenge on two of its steps
 * and two `<script>` tags for one library is a race over `window.turnstile`.
 */
export function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("turnstile script blocked")));
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Cleared, so a later mount retries rather than inheriting a settled
      // rejection for the rest of the session. A blocked script is as often a
      // flaky network as a permanent extension.
      scriptPromise = null;
      reject(new Error("turnstile script blocked"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export interface TurnstileState {
  /** Pass as `captchaToken`. `undefined` when the feature is off or nothing has been solved. */
  token: string | undefined;
  /** Feed to `<TurnstileWidget>`; bumping it re-mints. */
  resetKey: number;
  onToken: (token: string) => void;
  onUnavailable: () => void;
  /**
   * Call after **every** failed submit.
   *
   * A Turnstile token is single-use and short-lived, so a form rejected for an
   * unrelated reason - a wrong password, an address GoTrue would not take - is
   * left holding a spent one, and the next press fails as `captcha_failed` on a
   * password that was right. That reads as the product being broken, which is
   * worse than the original error it replaced.
   */
  reset: () => void;
}

export function useTurnstile(): TurnstileState {
  const [token, setToken] = React.useState("");
  const [resetKey, setResetKey] = React.useState(0);

  const reset = React.useCallback(() => {
    setToken("");
    setResetKey((key) => key + 1);
  }, []);

  // Blocked by an extension, a proxy or a Cloudflare outage. The form submits
  // anyway with no token - see the component for why this fails open.
  const onUnavailable = React.useCallback(() => setToken(""), []);

  return {
    token: token === "" ? undefined : token,
    resetKey,
    onToken: setToken,
    onUnavailable,
    reset,
  };
}
